import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { getAboutUs, signout } from "../../actions";
import BurgerMenu from "../BurgerMenu/BurgerMenu";
import ModalConfirm from "../BurgerMenu/ModalConfirm";
import UserInfoModal from "../../containers/UserInfoModal/UserInfoModal";
import NotifyToggle from "../NotifyToggle";
import AdminNotificationBell from "../Notifications/AdminNotificationBell";
import AdminRealtimePanelHeaderControl from "../../containers/Presence/AdminRealtimePanelHeaderControl";
import ThemeSwitcher from "../ThemeSwitcher/ThemeSwitcher";

const HEADER_COLLAPSED_KEY = "admin:header:collapsed";

const getInitialHeaderCollapsed = () => {
  if (typeof window === "undefined") return false;

  try {
    return window.localStorage.getItem(HEADER_COLLAPSED_KEY) === "true";
  } catch (error) {
    return false;
  }
};

export default function Header() {
  const auth = useSelector((s) => s.auth);
  const dispatch = useDispatch();

  const [showConfirm, setShowConfirm] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [collapsed, setCollapsed] = useState(getInitialHeaderCollapsed);

  const lastY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => { dispatch(getAboutUs()); }, [dispatch]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    document.documentElement.dataset.adminHeaderCollapsed = collapsed
      ? "true"
      : "false";
    try {
      window.localStorage.setItem(
        HEADER_COLLAPSED_KEY,
        collapsed ? "true" : "false"
      );
    } catch (error) {
      // Настройка всё равно работает до перезагрузки страницы.
    }

    return undefined;
  }, [collapsed]);

  useEffect(() => {
    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;
      requestAnimationFrame(() => {
        const y = window.scrollY || 0;
        const delta = y - lastY.current;
        const goingDown = delta > 6;
        const goingUp = delta < -6;
        if (window.innerWidth <= 640) {
          if (goingDown && y > 40) setHidden(true);
          if (goingUp) setHidden(false);
        } else {
          setHidden(false);
        }
        lastY.current = y;
        ticking.current = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const logout = () => dispatch(signout());

  return (
    <header
      className={[
        "app-header",
        hidden && !collapsed ? "app-header--hidden" : "",
        collapsed ? "app-header--collapsed" : "",
      ].filter(Boolean).join(" ")}
    >
      <ModalConfirm
        title="Выйти с профиля"
        show={showConfirm}
        handleClose={() => setShowConfirm(false)}
        onClick={logout}
      />

      <div className="app-header__inner">
        <div className="app-header__left">
          {auth.authenticate ? <BurgerMenu /> : null}
        </div>

        <div className="app-header__brand">
          <Link to="/" className="app-header__brand-link" title="Панель администратора">
            <span className="app-header__brand-text">ПАНЕЛЬ АДМИНИСТРАТОРА</span>
            <span className="app-header__brand-short">Админ</span>
          </Link>
        </div>
        <div className="app-header__right">
          <AdminNotificationBell />
          {auth.authenticate ? (
            <div className="app-header__theme">
              <ThemeSwitcher userThemeType={auth?.user?.siteThemeType} />
            </div>
          ) : null}
          {auth.authenticate ? <AdminRealtimePanelHeaderControl /> : null}
          {/* <NotifyToggle /> */}
          {auth.authenticate ? (
            <button
              className="app-header__btn app-header__btn--logout"
              onClick={() => setShowConfirm(true)}
              aria-label="Выйти"
              type="button"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path d="M16 17l5-5-5-5M21 12H9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M13 21H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <span className="app-header__btn-label">Выйти</span>
            </button>
          ) : (
            <Link to="/signin" className="app-header__btn app-header__btn--primary">
              Войти
            </Link>
          )}
        </div>
      </div>

      <button
        type="button"
        className="app-header__collapseToggle"
        aria-label={collapsed ? "Развернуть шапку" : "Свернуть шапку"}
        title={collapsed ? "Развернуть шапку" : "Свернуть шапку"}
        aria-expanded={!collapsed}
        onClick={() => {
          setHidden(false);
          setCollapsed((previous) => !previous);
        }}
      >
        <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
          <path
            d={collapsed ? "M7 14l5-5 5 5" : "M7 10l5 5 5-5"}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <UserInfoModal />
    </header>
  );
}
